import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { FareRulesDetailPayloadParts } from '@common/components/modals/my-booking-itinerary/fare-rules-detail/fare-rules-detail-payload.state';
import { buildPaymentDetailModalBlockParts } from '@common/components/modals/payment-detail/payment-detail-payload.state';
import { isEmptyObject, parseEmptyArray, stringEmptyDefault } from '@common/helpers';
import { DATA_CODE } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { FEATURE_TO_ROUTE_MAPPING } from '@common/helpers/constants/servicing.contant';
import { MyBookingTabShow } from '@common/helpers/enums/servicing.enum';
import {
  BookingDateFormat,
  MListData,
  PREVIOUS_PAGE_IDENTIFIER,
  ServicingFunctionIdType,
  ServicingPageIdType,
} from '@common/interfaces';
import { AircraftCabinAll } from '@common/interfaces/master/aircraft_cabin_all';
import { AirlineI18n, AirlineI18nAll } from '@common/interfaces/master/airline_i18n_all';
import { AirportI18n, AirportI18nAll } from '@common/interfaces/master/airport_i18n_all';
import { EquipmentI18nJoinPk, EquipmentI18nJoinPkByCode } from '@common/interfaces/master/equipment_pk';
import { FFPriorityCodeI18nAll } from '@common/interfaces/master/ff_priority_code_i18n_all';
import { MAwardCampaign } from '@common/interfaces/master/m_award-campaign.interface';
import { MFareRuleFull } from '@common/interfaces/master/m_fare_rule_full';
import {
  ServiceContentsByItineraryTypeCabin,
  ServiceContentsI18n,
} from '@common/interfaces/master/service_contents_by_itinerary_type_cabin';
import { TaxLangAll, TaxLangData } from '@common/interfaces/master/tax_lang_all';
import {
  FareConditionsBaggagePoliciesBaggageAllowance,
  FareConditionsCheckedBaggageAllowanceBaggageBoundsInner,
} from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import {
  ChargeableSeatRequestTaxes,
  CouchTaxes,
  FirstBaggageTaxes,
  KeepMyFareTaxes,
  LoungeTaxes,
  MealTaxes,
  Taxes,
  TotalTaxes,
  UnitPriceItem,
  UnitPrices,
} from '@common/interfaces/mybooking/order-prices.interface';
import { LOAD_MASTERS } from '@common/master';
import { ServicingCommonStoreService } from '@common/services';
import { GetFareConditionsStoreService } from '@common/services/my-booking/get-fare-conditions/get-fare-conditions.service';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { MyBookingStoreService } from '@common/services/my-booking/my-booking-store.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { GetFareConditionsState } from '@common/store/get-fare-conditions';
import { SupportComponent } from '@lib/components/support-class';
import { DialogClickType, LogType, MOffice, SessionStorageName } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes/static-msg/static-msg.pipe';
import {
  AswMasterService,
  CommonLibService,
  LoggerDatadogService,
  ModalService,
  PageInitService,
  PageLoadingService,
  SystemDateService,
} from '@lib/services';
import { BehaviorSubject, Subscription, combineLatest, forkJoin, map, of, take } from 'rxjs';
import {
  Bound,
  FareConditionsResponse,
  FareConditionsResponseData,
  FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner,
  GetOrderResponseData,
  GetOrderResponseDataAir,
  Traveler,
  Type1,
  UpdateBasicInformationSupportRequest,
} from 'src/sdk-servicing';

const MY_BOOKING_ITINERARY_MASTER_DATA_LIST = [
  LOAD_MASTERS.AIRLINE_I18N_ALL,
  LOAD_MASTERS.AIRPORT_I18N_ALL,
  LOAD_MASTERS.EQUIPMENT_I18NJOIN_PK,
  LOAD_MASTERS.SERVICE_CONTENTS_BY_ITINERARY_TYPE_CABIN,
  LOAD_MASTERS.M_OFFICE,
  LOAD_MASTERS.M_AWARD_CAMPAIGN,
  LOAD_MASTERS.TAX_ALL_LANG,
  LOAD_MASTERS.LISTDATA_ALL,
  LOAD_MASTERS.AIRCRAFT_CABIN_ALL,
  LOAD_MASTERS.M_FARE_FULL_RULE,
  LOAD_MASTERS.FF_PRIORITY_CODE_I18N_ALL,
];

type IMyBookingItineraryMasterData = [
  AirlineI18n,
  AirportI18n,
  EquipmentI18nJoinPkByCode,
  ServiceContentsByItineraryTypeCabin,
  MOffice[],
  MAwardCampaign[],
  TaxLangAll,
  MListData[],
  AircraftCabinAll[],
  MFareRuleFull[],
  FFPriorityCodeI18nAll[]
];

@Component({
  selector: 'asw-my-booking-itinerary-cont',
  templateUrl: './my-booking-itinerary-cont.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBookingItineraryContComponent extends SupportComponent {
  // 注文応答データの取得
  orderData!: GetOrderResponseData;

  // 画面ID
  pageId: string = ServicingPageIdType.MYBOOKING_ITINERARY;
  // 機能ID
  functionId: string = ServicingFunctionIdType.BOOKING_CONFIRMATION;

  // 言語コード
  get aswLangCode() {
    return this._common.aswContextStoreService.aswContextData.lang;
  }

  private _subscriptions: Subscription = new Subscription();
  isInitComplete$ = new BehaviorSubject(false);
  fareCondition!: GetFareConditionsState;
  flights: Array<Type1> = [];
  travelers: Array<Traveler> = [];

  // 車いす情報リスト
  wheelchairInformationList: any[] = []; // Just have 1 item

  private mappedAirportI18nAllMaster: AirportI18nAll[] = [];
  private mappedMAwardCampaignMaster: MAwardCampaign[] = [];
  private mappedAirlineI18nAllMaster: AirlineI18nAll[] = [];
  private mappedMListDataAllMaster: MListData[] = [];
  private mappedFFPriorityCodeI18nAllMaster: FFPriorityCodeI18nAll[] = [];

  constructor(
    private readonly _common: CommonLibService,
    private readonly _pageInitService: PageInitService,
    private readonly _myBookingItineraryService: MyBookingItineraryService,
    private readonly _router: Router,
    private readonly _myBookingServiceService: MyBookingServiceService,
    private readonly _aswMasterService: AswMasterService,
    private readonly _modal: ModalService,
    private readonly _fareConditionsStoreService: GetFareConditionsStoreService,
    private readonly _datePipe: DatePipe,
    private readonly _servicingCommonStoreService: ServicingCommonStoreService,
    public readonly _change: ChangeDetectorRef,
    private readonly _loggerDatadogService: LoggerDatadogService,
    private readonly _myBookingService: MyBookingService,
    private readonly _pageLoadingService: PageLoadingService,
    private readonly _myBookingStoreService: MyBookingStoreService,
    private readonly _staticMsgPipe: StaticMsgPipe,
    private readonly _systemDateSvc: SystemDateService
  ) {
    super(_common);
  }

  /** (1) 1.	画面の初期化処理(G01-003)の初期化開始処理を呼び出す。*/
  screenInit() {
    const identificationId = sessionStorage.getItem(SessionStorageName.IDENTIFICATION_ID);
    this._loggerDatadogService.info(LogType.PAGE_VIEW, {
      identificationId,
      functionId_pageId: `${this.functionId}_${this.pageId}`,
    });
  }

  onInit() {
    this._pageLoadingService.startLoading(true);
    this._subscriptions.add(
      forkJoin([
        this._common.aswCommonStoreService.getAswCommon$().pipe(take(1)),
        this._aswMasterService.load(MY_BOOKING_ITINERARY_MASTER_DATA_LIST, true).pipe(
          map((res) => res as IMyBookingItineraryMasterData),
          take(1)
        ),
        this._servicingCommonStoreService.getServicingCommonModel$().pipe(take(1)),
      ]).subscribe((res) => {
        const [
          aswCommon,
          [
            airlineI18nAllMaster,
            airportI18nAllMaster,
            equipmentI18nJoinPkMaster,
            serviceContentsByItineraryTypeCabinMaster,
            mOfficeMaster,
            mAwardCampaignMaster,
            taxLangAllMaster,
            mListDataAllMaster,
            aircraftCabinAllMaster,
            mFareRuleFullMaster,
            fFPriorityCodeI18nAllMaster,
          ],
          servicingCommon,
        ] = res;
        this.orderData = servicingCommon.getOrder as GetOrderResponseData;
        this.flights =
          this.orderData?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[]);
        this.travelers = this.orderData?.travelers ?? ([] as Traveler[]);

        this.screenInit();

        // (1) 2. 機能ID≠"S01"、または画面ID≠"P031"の場合、直近の遷移元画面情報を保存するため以下に値を設定する。
        const previousPage = `${aswCommon?.functionId ?? ''}-${aswCommon?.pageId ?? ''}`;
        if (aswCommon?.functionId !== this.functionId && aswCommon?.pageId !== this.pageId) {
          this._myBookingItineraryService.transitionSourceFullFunctionPageId = previousPage;
          this._myBookingItineraryService.transitionSourceFunctionId = aswCommon?.functionId ?? '';
          this._myBookingItineraryService.transitionSourcePageId = aswCommon?.pageId ?? '';
        }

        // (1) 3. 画面情報に以下の内容を設定する。
        this._common.aswCommonStoreService.updateAswCommon({
          functionId: this.functionId,
          pageId: this.pageId,
          subFunctionId: '',
          subPageId: '',
          isEnabledLogin: false,
          isUpgrade: false,
        });

        this.mappedAirportI18nAllMaster = parseEmptyArray(
          Object.values(airportI18nAllMaster).map((value) => value?.[0])
        );
        this.mappedAirlineI18nAllMaster = parseEmptyArray(
          Object.values(airlineI18nAllMaster).map((value) => value?.[0])
        );
        const mappedEquipmentI18nJoinPkMaster = parseEmptyArray(
          Object.values(equipmentI18nJoinPkMaster).map((value) => value?.[0])
        );
        this.mappedFFPriorityCodeI18nAllMaster = parseEmptyArray(fFPriorityCodeI18nAllMaster);
        this.mappedMAwardCampaignMaster = parseEmptyArray(mAwardCampaignMaster);
        const mappedTaxLangAllMaster = parseEmptyArray(taxLangAllMaster?.[this.aswLangCode]);
        this.mappedMListDataAllMaster = parseEmptyArray(mListDataAllMaster);
        const mappedServiceContentsByItineraryTypeCabinMater = serviceContentsByItineraryTypeCabinMaster ?? {};
        const mappedMOfficeMaster = parseEmptyArray(mOfficeMaster);
        const mappedMFareRuleFullMaster = parseEmptyArray(mFareRuleFullMaster);
        const mappedAircraftCabinAllMaster = parseEmptyArray(aircraftCabinAllMaster);

        this._myBookingItineraryService.masterData$.next({
          ...this._myBookingItineraryService.masterData$.value,
          airportI18nAll: this.getAirPortFromMAirpotI18N(this.mappedAirportI18nAllMaster),
          mAwardCampaign: this.geMAwardCampaignList(this.mappedMAwardCampaignMaster),
          internationalCarrierCodes: this.getInternationalCarrierCodes(this.mappedAirlineI18nAllMaster),
          mListDataPD800: this.getSeasonalityList(this.mappedMListDataAllMaster),
          fFPriorityCodeAll: this.getMFFPriorityCodeList(this.mappedFFPriorityCodeI18nAllMaster),
          carrierList: this.getCarrierList(this.mappedAirlineI18nAllMaster),
          equipmentI18nJoinPk: this.getMEquipmentList(mappedEquipmentI18nJoinPkMaster),
          internationalFFPriorityCodes: this.getInternationalFFPriorityCodes(this.mappedFFPriorityCodeI18nAllMaster),
          serviceContents: this.getServiceContents(mappedServiceContentsByItineraryTypeCabinMater),
          mOffice: this.getMOfficeList(mappedMOfficeMaster),
          taxLangData: this.getMTaxI18NList(mappedTaxLangAllMaster),
          flightInfoReasonCodes: this.getFlightInfoReasonList(this.mappedMListDataAllMaster),
          //(1) 17.	運賃フルルール情報リスト取得処理
          mFareRuleFull: mappedMFareRuleFullMaster,
          aircraftCabinAll: this.getMAircraftCabinList(mappedAircraftCabinAllMaster),
          mListDataPD113: this.getMListDataPD113(this.mappedMListDataAllMaster),
        });
        this._change.markForCheck();
        this.isInitComplete$.next(true);

        /** Save list function of Event to Service to using in child component */
        this._myBookingItineraryService.functionList = this.getFunctionList;
        this._pageInitService.endInit(
          of({
            getOrderReply: { data: this.orderData },
            aswContext: this._common.aswContextStoreService.aswContextData,
            creationDateTime: this._systemDateSvc.getFormattedSystemDate('yyyy-MM-ddTHH:mm:ss'),
            // 直前のページを渡す
            prePage: this._servicingCommonStoreService.servicingCommon?.previousPageIdentifier,
          })
        );
        this._pageLoadingService.endLoading(true);
      })
    );

    this._subscriptions.add(
      combineLatest([
        this._fareConditionsStoreService.getFareConditionsObservable(),
        this._myBookingService.apiGetFareConditionSubject.asObservable(),
        this.isInitComplete$.asObservable(),
      ]).subscribe((res) => {
        const [fareCondition, apiGetFareConditionSubject, isInitComplete] = res;
        if (
          apiGetFareConditionSubject &&
          isInitComplete &&
          !fareCondition?.isPending &&
          !isEmptyObject(fareCondition?.model?.data)
        ) {
          this.fareCondition = fareCondition ?? ({} as GetFareConditionsState);
          this._myBookingItineraryService.masterData$.next({
            ...this._myBookingItineraryService.masterData$.value,
            airportI18nAll: this.getAirPortFromMAirpotI18N(this.mappedAirportI18nAllMaster),
            mAwardCampaign: this.geMAwardCampaignList(this.mappedMAwardCampaignMaster),
            internationalCarrierCodes: this.getInternationalCarrierCodes(this.mappedAirlineI18nAllMaster),
            mListDataPD800: this.getSeasonalityList(this.mappedMListDataAllMaster),
            fFPriorityCodeAll: this.getMFFPriorityCodeList(this.mappedFFPriorityCodeI18nAllMaster),
          });
        }
      })
    );
  }

  /** 初期表示処理 */
  init() {
    this._subscriptions.add(
      this._myBookingService.onInitFinishedSubject.subscribe((initFinished) => {
        if (initFinished) {
          this.onInit();
        }
      })
    );
  }

  /** (1) 4. 空港リスト取得処理 */
  getAirPortFromMAirpotI18N(master: AirportI18nAll[]): AirportI18nAll[] {
    const travelers: Traveler[] = this.orderData?.travelers ?? ([] as Traveler[]);
    const fareCondition = this.fareCondition?.model ?? ({} as FareConditionsResponse);
    // 4.1.	空の空港コードリストを作成する
    const airportList: string[] = [];
    // <以下、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し>(それぞれの要素が当該バウンド)
    // <以下、当該バウンド.flights[]の要素数繰り返し> (それぞれの要素が当該セグメント情報)
    // 4.2.	空港コードリストに当該セグメント情報.depature.locationCodeを追加する
    // 4.3. 空港コードリストに当該セグメント情報.arrival.locationCodeを追加する
    this.flights.forEach((item: Type1) => {
      item?.departure?.locationCode && airportList.push(item.departure.locationCode);
      item?.arrival?.locationCode && airportList.push(item.arrival.locationCode);
      // <以下、当該セグメント情報.stops[]の要素数繰り返し> (それぞれの要素が当該途中寄港地点情報)
      // 4.4.空港コードリストに当該途中寄港地点情報.locationCodeを追加する
      item?.stops?.forEach((stop) => {
        stop?.locationCode && airportList.push(stop?.locationCode);
      });
    });

    // <以下、PNR情報取得API応答.data.travelers[]の要素数繰り返し>(それぞれの要素が当該搭乗者情報)
    travelers.forEach((traveler: Traveler) => {
      // <以下、運賃ルール・手荷物情報取得API結果.data.baggagePolicies.baggageAllowance.<当該搭乗者情報.id>.baggageBounds[]の要素数繰り返し>(それぞれの要素が当該適用区間情報)
      // 4.7.	空港コードリストに当該適用区間情報.departure.locationCodeを追加する
      // 4.8.	空港コードリストに当該適用区間情報.arrival.locationCodeを追加する
      const baggageBoundsByTravelerId =
        (((fareCondition.data?.baggagePolicies?.baggageAllowance as any)?.[String(traveler?.id)] as any)
          ?.baggageBounds as any[]) ?? ([] as any[]);
      baggageBoundsByTravelerId.forEach((item) => {
        item?.departure?.locationCode && airportList.push(item?.departure?.locationCode);
        item?.arrival?.locationCode && airportList.push(item?.arrival?.locationCode);
      });
    });

    // <以下、運賃ルール・手荷物情報取得API応答.data.airOfferConditions.airOfferConditionBounds[]の件数分繰り返し＞ (それぞれの要素が当該運賃ルール用のバウンド)
    // 4.9.	空港コードリストに当該運賃ルール用のバウンド.originLocationCodeを追加する
    // 4.10.	空港コードリストに当該運賃ルール用のバウンド.destinationLocationCodeを追加する
    // <ここまで、運賃ルール・手荷物情報取得API応答.data.airOfferConditions.airOfferConditionBounds[]の要素数繰り返し>
    fareCondition.data?.airOfferConditions?.airOfferConditionBounds?.forEach((airOfferConditionBound) => {
      airOfferConditionBound?.originLocationCode && airportList.push(airOfferConditionBound?.originLocationCode);
      airOfferConditionBound?.destinationLocationCode &&
        airportList.push(airOfferConditionBound?.destinationLocationCode);
    });

    // 4.11.	以下の条件でASWDB(マスタ)から空港(国際化)を取得し、空港リストとして保持する。
    return master.filter((item) => {
      return airportList.indexOf(item?.airport_code) !== -1 && item?.lang === this.aswLangCode;
    });
  }

  /** (1) 5. キャリアリスト取得処理 */
  getCarrierList(master: AirlineI18nAll[]): AirlineI18nAll[] {
    // 5.1.	空のキャリアコードリストを作成する
    const airlineCodeLists: string[] = [];

    this.orderData?.air?.bounds?.forEach((bound: Bound) => {
      bound.flights?.forEach((flight: Type1) => {
        // 5.2.	キャリアコードリストに当該セグメント情報.operatingAirlineCodeを追加する。
        flight?.operatingAirlineCode && airlineCodeLists.push(flight.operatingAirlineCode);
      });
    });
    // 5.3.	以下の条件でASWDB(マスタ)からキャリアを取得し、キャリアリストとして保持する。
    return master?.filter((item) => airlineCodeLists.indexOf(item?.airline_code) !== -1);
  }

  /** (1) 6. 機材リスト取得処理 */
  getMEquipmentList(master: EquipmentI18nJoinPk[]): EquipmentI18nJoinPk[] {
    // 6.1.	空のACVリストを作成する
    const aircraftConfigurationVersions: string[] = [];

    this.flights.forEach((item: Type1) => {
      // 6.2.	ACVリストに当該セグメント情報.aircraftConfigurationVersionを追加する。
      item?.aircraftConfigurationVersion && aircraftConfigurationVersions.push(item.aircraftConfigurationVersion);
    });

    // 6.3.	以下の条件でASWDB(マスタ)から機材を取得し、機材リストとして保持する。
    return master?.filter((item) => aircraftConfigurationVersions.indexOf(item?.acv) !== -1);
  }

  /** (1) 7. FFPriority(国際化)リスト取得処理 */
  getInternationalFFPriorityCodes(master: FFPriorityCodeI18nAll[]): FFPriorityCodeI18nAll[] {
    // 7.1.	空のFFPriorityコードリストを作成する
    const FFPriorityCodes: string[] = [];
    // 7.2.	4桁まで当該セグメント情報.fareInfos.priorityCodeの前に"0"をつけて、FFPriorityコードリストに追加する。
    this.flights.forEach((item: Type1) => {
      if (item?.fareInfos?.priorityCode) {
        const priorityCode = item.fareInfos.priorityCode.toString().padStart(4, '0');
        FFPriorityCodes.push(priorityCode);
      }
    });
    // 7.3.	以下の条件でASWDB(マスタ)からFF Priority Code(国際化)を取得し、FFPriorityリストとして保持する。
    return master?.filter(
      (item: FFPriorityCodeI18nAll) =>
        FFPriorityCodes.indexOf(item?.ff_priority_code ?? '') !== -1 && item?.lang === this.aswLangCode
    );
  }

  /** (1) 9.	サービスコンテンツリスト取得処理 */
  getServiceContents(serviceContentsByItineraryTypeCabin: ServiceContentsByItineraryTypeCabin): ServiceContentsI18n[] {
    // 9.1.	空のCabin Classリストを作成する
    const CabinClasses = Array.from(new Set(this.flights.map((item) => item.cabin ?? '')));
    return CabinClasses.reduce((acc, key) => {
      if (serviceContentsByItineraryTypeCabin?.['I']?.[key]) {
        acc = acc.concat((serviceContentsByItineraryTypeCabin as any)?.['I']?.[key]);
      }
      if (serviceContentsByItineraryTypeCabin?.['D']?.[key]) {
        acc = acc.concat((serviceContentsByItineraryTypeCabin as any)?.['D']?.[key]);
      }
      return acc;
    }, []);
  }

  /** (1) 10. オフィス情報取得処理 */
  getMOfficeList(master: MOffice[]): MOffice[] {
    return master?.filter(
      (item) => item?.office_code === this._common?.aswContextStoreService?.aswContextData?.pointOfSaleId
    );
  }

  /** (1) 11. 特典キャンペーンリスト取得処理 */
  geMAwardCampaignList(master: MAwardCampaign[]): MAwardCampaign[] {
    const fareCondition = this.fareCondition?.model ?? ({} as FareConditionsResponse);
    // 11.1.	空の特典キャンペーン対象FAREBASISリストを作成する
    const fareBasisList: string[] = [];
    // <以下、運賃ルール・手荷物情報取得API結果.data.airOfferConditions.airOfferConditionBounds[]の要素数繰り返し>(それぞれの要素が当該運賃ルール用のバウンド)
    // 11.2.	 特典キャンペーン対象FAREBASISリストに当該運賃ルール用のバウンド.fareBasisを追加する。
    const airOfferConditionBounds =
      fareCondition?.data?.airOfferConditions?.airOfferConditionBounds ??
      ([] as FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner[]);
    airOfferConditionBounds.forEach((item) => {
      item?.fareBasis && fareBasisList.push(item.fareBasis);
    });

    // 11.3.	以下の条件でASWDB(マスタ)から特典キャンペーンを取得し、特典キャンペーンリストとして保持する。
    return master.filter(
      (item) => fareBasisList.indexOf(item?.farebasis_code) !== -1 && item?.lang === this.aswLangCode
    );
  }

  /** (1) 12.	キャリア(国際化)リスト取得処理 */
  getInternationalCarrierCodes(master: AirlineI18nAll[]): AirlineI18nAll[] {
    const travelers: Traveler[] = this.orderData?.travelers ?? ([] as Traveler[]);
    const fareCondition = this.fareCondition?.model ?? ({} as FareConditionsResponse);
    // 12.1.	空のキャリアコード(国際化)リストを作成する
    const airlineCodes: string[] = [];
    // <以下、PNR情報取得API応答.data.travelers[]の要素数繰り返し>(それぞれの要素が当該搭乗者情報)
    travelers.forEach((traveler) => {
      const baggageAllowance =
        (fareCondition?.data?.baggagePolicies?.baggageAllowance as FareConditionsBaggagePoliciesBaggageAllowance) ??
        ({} as FareConditionsBaggagePoliciesBaggageAllowance);
      const baggageBoundsByTravelerId =
        (baggageAllowance?.[String(traveler?.id)]
          ?.baggageBounds as FareConditionsCheckedBaggageAllowanceBaggageBoundsInner[]) ??
        ([] as FareConditionsCheckedBaggageAllowanceBaggageBoundsInner[]);

      /** <以下、運賃ルール・手荷物情報取得API結果.data.baggagePolicies.baggageAllowance.<当該搭乗者情報.id>.baggageBounds[]の要素数繰り返し>(それぞれの要素が当該適用区間情報) */
      baggageBoundsByTravelerId.forEach((item) => {
        /** 12.2. キャリアコード(国際化)リストに当該適用区間情報.checkedBaggageAllowance.airlineCodeを追加する */
        item?.checkedBaggageAllowance?.airlineCode && airlineCodes.push(item?.checkedBaggageAllowance?.airlineCode);
        /** 12.3. キャリアコード(国際化)リストに当該適用区間情報.carryOnAllowance.airlineCodeを追加する */
        item?.carryOnAllowance?.airlineCode && airlineCodes.push(item?.carryOnAllowance?.airlineCode);
      });
      /** <ここまで、運賃ルール・手荷物情報取得API結果.data.baggagePolicies.baggageAllowance.<当該搭乗者情報.id>.baggageBounds[]の要素数繰り返し> */
    });

    // <以下、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し>(それぞれの要素が当該バウンド)
    // <以下、当該バウンド.flights[]の要素数繰り返し> (それぞれの要素が当該セグメント情報)
    // 12.3.	キャリアコード(国際化)リストに当該セグメント情報.operatingAirlineCodeを追加する
    this.flights.forEach((item: Type1) => {
      item?.operatingAirlineCode && airlineCodes.push(item.operatingAirlineCode);
    });

    // 12.4.	以下の条件でASWDB(マスタ)からキャリア(国際化)を取得し、キャリアリストとして保持する。
    return master.filter((item) => airlineCodes.indexOf(item?.airline_code) !== -1 && item?.lang === this.aswLangCode);
  }

  /** (1) 13.	税金リスト取得処理 */
  getMTaxI18NList(master: TaxLangData[]): TaxLangData[] {
    const travelers: Traveler[] = this.orderData?.travelers ?? ([] as Traveler[]);
    // 13.1. 空の 税金コードリストを作成する
    let taxeCodeList: string[] = [];
    // <以下、PNR情報取得API応答.data.travelers[]の要素数繰り返し>(それぞれの要素が当該搭乗者情報)
    travelers.forEach((traveler: Traveler) => {
      const unitPricesByTraveler: UnitPriceItem =
        (this.orderData?.prices?.unitPrices as UnitPrices)?.[String(traveler?.id)] ?? ({} as UnitPriceItem);

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.ticketPrices.taxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const taxes: Taxes[] = unitPricesByTraveler?.ticketPrices?.taxes ?? ([] as Taxes[]);
      // 13.2. 税金コードリストに当該支払情報.codeを追加する
      taxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.servicePrices.chargeableSeatRequestTaxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const chargeableSeatRequestTaxes: ChargeableSeatRequestTaxes[] =
        unitPricesByTraveler?.servicePrices?.chargeableSeatRequestTaxes ?? ([] as ChargeableSeatRequestTaxes[]);
      // 13.3. 税金コードリストに当該支払情報.codeを追加する
      chargeableSeatRequestTaxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.servicePrices.couchTaxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const couchTaxes: CouchTaxes[] = unitPricesByTraveler?.servicePrices?.couchTaxes ?? ([] as CouchTaxes[]);
      // 13.4.	税金コードリストに当該支払情報.codeを追加する
      couchTaxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.servicePrices.firstBaggageTaxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const firstBaggageTaxes: FirstBaggageTaxes[] =
        unitPricesByTraveler?.servicePrices?.firstBaggageTaxes ?? ([] as FirstBaggageTaxes[]);
      // 13.5.	税金コードリストに当該支払情報.codeを追加する
      firstBaggageTaxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.servicePrices.loungeTaxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const loungeTaxes: LoungeTaxes[] = unitPricesByTraveler?.servicePrices?.loungeTaxes ?? ([] as LoungeTaxes[]);
      // 13.6.	税金コードリストに当該支払情報.codeを追加する
      loungeTaxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.servicePrices.mealTaxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const mealTaxes: MealTaxes[] = unitPricesByTraveler?.servicePrices?.mealTaxes ?? ([] as MealTaxes[]);
      // 	13.7.	税金コードリストに当該支払情報.codeを追加する
      mealTaxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.servicePrices.keepMyFareTaxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const keepMyFareTaxes: KeepMyFareTaxes[] =
        unitPricesByTraveler?.servicePrices?.keepMyFareTaxes ?? ([] as KeepMyFareTaxes[]);
      // 13.8.	税金コードリストに当該支払情報.codeを追加する
      keepMyFareTaxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });

      // <以下、PNR情報取得API応答.data.prices.unitPrices.<当該搭乗者情報.id>.paidKeepMyFarePrices.totalTaxes[]の要素数繰り返し>（それぞれの要素が当該支払情報）
      const totalTaxes: TotalTaxes[] = unitPricesByTraveler?.paidKeepMyFarePrices?.totalTaxes ?? ([] as TotalTaxes[]);
      // 13.9.	税金コードリストに当該支払情報.codeを追加する
      totalTaxes.forEach((item) => {
        item?.code && taxeCodeList.push(item.code);
      });
    });

    // 13.10.	税金コードリストに"F", "IS", "FS", "TF","YQ"を追加する。
    taxeCodeList = taxeCodeList.concat(['F', 'IS', 'FS', 'OB', 'YQ']);

    // 	13.11.	以下の条件でASWDB(マスタ)から税金(国際化)を取得し、税金リストとして保持する。
    return master?.filter(
      (item) => taxeCodeList.indexOf(stringEmptyDefault(item?.tax_code).trim()) != -1 && item?.lang === this.aswLangCode
    );
  }

  /** (1) 14.	シーズナリティリスト取得処理 */
  getSeasonalityList(master: MListData[]): MListData[] {
    const fareCondition = this.fareCondition?.model ?? ({} as FareConditionsResponse);
    // 14.1. 空のシーズナリティ情報を作成する
    const seasonalityList: string[] = [];

    // <以下、運賃ルール・手荷物情報取得API結果.data.airOfferConditions.airOfferConditionBounds[]の要素数繰り返し>(それぞれの要素が当該運賃ルール用のバウンド)
    const airOfferConditionBounds =
      fareCondition?.data?.airOfferConditions?.airOfferConditionBounds ??
      ([] as FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner[]);
    // 14.2. シーズナリティ情報に当該運賃ルール用のバウンド.seasonalityを追加する
    airOfferConditionBounds.forEach((item) => {
      item?.seasonality && seasonalityList.push(item.seasonality);
    });

    // 14.3. 以下の条件でASWDB(マスタ)から汎用マスターデータ(リスト)を取得し、シーズナリティリストとして保持する。
    return master.filter(
      (item) =>
        item?.data_code === DATA_CODE.PD_800 &&
        seasonalityList.indexOf(item?.value) !== -1 &&
        item?.lang === this.aswLangCode
    );
  }

  /** (1) 16. 運航情報理由リスト取得処理 */
  getFlightInfoReasonList(master: MListData[]): MListData[] {
    const bounds: Bound[] = this.orderData?.air?.bounds ?? ([] as Bound[]);
    // 	16.1.	空の運航情報理由コードリストを作成する
    let flightInfoReasonCodes: string[] = [];

    // 16.2. PNR情報取得API応答結果.data.air.bounds[].cancel.reasonCodes[]が存在する場合、
    // 16.3. PNR情報取得API応答結果.data.air.bounds[].delay.reasonCodes[]が存在する場合、
    // 16.4. PNR情報取得API応答結果.data.air.bounds[].earlyDeparture.reasonCodes[]が存在する場合、
    // <以下、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し>(それぞれの要素が当該バウンド)
    bounds.forEach((bound: Bound) => {
      const reasonCancelCodeList: string[] = bound?.cancel?.reasonCodes ?? [];
      const reasonDelayCodeList: string[] = bound?.delay?.reasonCodes ?? [];
      const reasonEarlyDepartCodeList: string[] = bound?.earlyDeparture?.reasonCodes ?? [];
      // 	16.2.1.	運航情報理由コードリストに当該バウンド.cancel.reasonCodes[]を追加する
      flightInfoReasonCodes = flightInfoReasonCodes.concat(reasonCancelCodeList);
      // 	16.3.1.	運航情報理由コードリストに当該バウンド.delay.reasonCodes[]を追加する
      flightInfoReasonCodes = flightInfoReasonCodes.concat(reasonDelayCodeList);
      // 	16.4.1.	運航情報理由コードリストに当該バウンド.earlyDeparture.reasonCodes[]を追加する
      flightInfoReasonCodes = flightInfoReasonCodes.concat(reasonEarlyDepartCodeList);
    });

    // 	16.5.	運航情報理由コードリストの重複な要素を除く
    flightInfoReasonCodes = Array.from(new Set(flightInfoReasonCodes));

    // 	16.6.	以下の条件でASWDB(マスタ)からを取得し、運航情報理由リストとして保持する。
    return master.filter((item: MListData) => {
      return (
        // 汎用マスターデータ(リスト).データコード = "PD_710"
        item?.data_code === DATA_CODE.PD_710 &&
        // 汎用マスターデータ(リスト).value IN 運航情報理由コードリスト
        flightInfoReasonCodes.indexOf(item?.value) !== -1 &&
        // 汎用マスターデータ(リスト).言語コード=ユーザ共通情報.data.aswContext.lang
        item?.lang === this.aswLangCode
      );
    });
  }

  /** (1) 18. キャビンクラス別機材情報リスト得処理 */
  getMAircraftCabinList(master: AircraftCabinAll[]): AircraftCabinAll[] {
    // 18.1.	空のキャビンクラスACV情報リストを作成する
    const aircraftConfigurationVersions: string[] = [];

    // <以下、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し>(それぞれの要素が当該バウンド)
    // <以下、当該バウンド.flights[]の要素数繰り返し> (それぞれの要素が当該セグメント情報)
    this.flights.forEach((item: Type1) => {
      // 18.2. キャビンクラスACV情報リストに当該セグメント情報.aircraftConfigurationVersionを追加する
      item?.aircraftConfigurationVersion && aircraftConfigurationVersions.push(item.aircraftConfigurationVersion);
    });

    // 18l.3.	以下の内容でASWDB(マスタ)よりキャビンクラス別機材情報を取得し、キャビンクラス別機材情報リストとして保持する。
    return master?.filter((item) => aircraftConfigurationVersions.indexOf(item?.acv) !== -1);
  }

  /** (1) 19. 表示内容取得処理 */
  getMListDataPD113(master: MListData[]): MListData[] {
    // 	19.1.	以下の条件でASWDB(マスタ)から汎用マスターデータ(リスト)を取得し、表示内容として保持する。
    return master?.filter(
      (item) =>
        // 汎用マスターデータ(リスト).データコード = "PD_113" AND
        item.data_code === DATA_CODE.PD_113 &&
        // 汎用マスターデータ(リスト).value = ユーザ共通情報.data.aswContext.posCountryCode AND
        item.value === this._common.aswContextStoreService.aswContextData.posCountryCode &&
        // 汎用マスターデータ(リスト).言語コード = ユーザ共通情報.data.aswContext.lang
        item.lang === this.aswLangCode
    );
  }

  /** (1) 20. FFPriorityリスト取得処理 */
  getMFFPriorityCodeList(master: FFPriorityCodeI18nAll[]): FFPriorityCodeI18nAll[] {
    const fareCondition = this.fareCondition?.model ?? ({} as FareConditionsResponse);
    // 20.1.	空のFFPriorityコードリストを作成する
    const FFPriorityCodeList: string[] = [];
    // <以下、運賃ルール・手荷物情報取得API結果.data.airOfferConditions.airOfferConditionBounds[]の要素数繰り返し>(それぞれの要素が当該運賃ルール用のバウンド)
    const airOfferConditionBounds: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner[] =
      fareCondition?.data?.airOfferConditions?.airOfferConditionBounds ??
      ([] as FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner[]);
    airOfferConditionBounds.forEach(
      (item: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner) => {
        // 20.2.	FFPriorityコードリストに当該運賃ルール用のバウンド.priorityCodeを追加する。
        item?.priorityCode && FFPriorityCodeList.push(item.priorityCode);
      }
    );

    return master.filter(
      (item: FFPriorityCodeI18nAll) => FFPriorityCodeList.indexOf(item.ff_priority_code ?? '') !== -1
    );
  }

  /** (2) 「出発地までの経路案内」リンク押下時 */
  async clickGuidanceToDeparture(airBoundId: string, flightID: string) {
    // 1. & 2.
    const message = this.getMsgDialogByLanguage();
    const result = await this._myBookingServiceService.openDialogPromise(message);
    // 3.「確定」ボタンを押下した場合、確認ダイアログ上で以下の処理を行う。
    if (result === DialogClickType.CONFIRM) {
      /**
       * 3.1 [当画面共通処理No1.]を行う。
       * バウンドID: 当該バウンドID
       * セグメントID: 当該セグメントID
       * 対象便種別: "1"(出発便)
       */
      this.onHandleCommonNo1(airBoundId, flightID, 1);
    }

    // 4.	「キャンセル」ボタンを押下した場合、確認ダイアログ上でダイアログを閉じる。
    if (result === DialogClickType.CLOSE) {
      return;
    }
  }

  /** (3)「「座席指定状況」ボタン押下時 */
  clickSeatReservationStatus(flightId: string) {
    // 1.	当該セグメントのシートマップ(S03-P010)へ遷移する。以下の内容をパラメータとして指定し、シートマップ(S03-P010)へ遷移する。
    this._servicingCommonStoreService.updateServicingCommon({ displayTargetSegmentId: flightId });
    this._router.navigate([FEATURE_TO_ROUTE_MAPPING['S03-P010']]);
  }

  /** (4)「到着地からの経路案内」リンク押下時 */
  async clickGuidanceToDestination(airBoundId: string, flightID: string) {
    const message = this.getMsgDialogByLanguage();
    const result = await this._myBookingServiceService.openDialogPromise(message);
    // 3.「「確定」ボタンを押下した場合、確認ダイアログ上で以下の処理を行う。
    if (result === DialogClickType.CONFIRM) {
      /**
       * 3.1 [当画面共通処理No1.]を行う。
       * バウンドID: 当該バウンドID
       * セグメントID: 当該セグメントID
       * 対象便種別: "2"(出発便)
       */
      this.onHandleCommonNo1(airBoundId, flightID, 2);
    }

    // 4.「「キャンセル」ボタンを押下した場合、確認ダイアログ上でダイアログを閉じる。
    if (result === DialogClickType.CLOSE) {
    }
  }

  /** (5)「「支払情報詳細モーダル表示」ボタン押下時 */
  clickOpenPaymentDetailsModal() {
    const part = buildPaymentDetailModalBlockParts();
    // 1.	支払情報詳細モーダルを表示する。
    this._modal.showSubModal(part);
  }

  /** (12)「運賃ルール詳細モーダル表示」ボタン押下時 */
  clickFareRuleDetailsModal(): void {
    /**
     * 1.	PNR情報取得API応答.data.orderType.isAwardBooking = false(有償PNR)の場合、運賃ルール詳細をモーダルで表示する。
     * 2. PNR情報取得API応答.data.orderType.isAwardBooking = true(特典PNR)の場合、設定されたコンテンツページを別タブで表示する。
     */
    if (!this.orderData?.orderType?.isAwardBooking) {
      this.openFareRulesModal();
    } else {
      this._myBookingItineraryService.openFareRuleOnNewTab(
        this.orderData?.orderType?.isAwardBooking,
        this.fareCondition?.model?.data ?? ({} as FareConditionsResponseData)
      );
    }
  }

  openFareRulesModal() {
    this._fareConditionsStoreService
      .getFareConditionsObservable()
      .pipe(take(1))
      .subscribe((getFareConditionsState) => {
        const fareConditions = getFareConditionsState?.model?.data ?? ({} as FareConditionsResponseData);
        const part = FareRulesDetailPayloadParts();
        part.payload = {
          fareConditions: fareConditions,
          orderData: this.orderData,
        };
        this._modal.showSubModal(part);
      });
  }

  /** (15) 「手荷物申込モーダル表示」ボタン押下時 */
  clickBaggageApplication(): void {
    // 1.	予約詳細表示タブ=”service”(予約詳細(サービス))を設定し、[表示タブ切り替え処理]を行う。
    const tabShow = MyBookingTabShow.MyBookingTabShowEnum.Service;
    this._myBookingService.tabShow = tabShow;
    this._myBookingService.handleChangeTab(tabShow);
    // 2.	手荷物申込(S02-M016)をモーダルで表示する。
    this._myBookingServiceService.openBaggageApplicationModal();
  }

  /**
   *
   * @param flightIndex this is the index of the flight list of all bound
   * @returns
   */

  getReferrer(): string {
    const isAnaApl: boolean = this._common?.aswContextStoreService?.aswContextData?.isAnaApl;
    const tripType: string = this.orderData?.air?.tripType ?? '';
    // 1)ユーザ共通情報.data.aswContext.isAnaApl=trueの場合、PNR情報取得API応答.data.air.tripTypeに応じて以下
    if (isAnaApl) {
      // 1.1)“international”の場合、“appint”
      if (tripType === GetOrderResponseDataAir.TripTypeEnum.International) {
        return 'appint';
      }
      // 1.2)上記以外の場合、”appdms”
      return 'appdms';
    }
    // 2)上記以外の場合、PNR情報取得API応答.data.air.tripTypeに応じて以下
    else {
      // 2.1)“international”の場合、“aswint”
      if (tripType === GetOrderResponseDataAir.TripTypeEnum.International) {
        return 'aswint';
      }
      // 2.2)上記以外の場合、”aswdms”
      // 提携: Redmine#47867 Airdo様サイトからのパラメータ連携仕様書.pptxより referrerをADO用("airdo")に修正
      return 'airdo';
    }
  }

  getDateTime(dateTime?: string, formatDate?: string): string {
    return this._datePipe.transform(dateTime, formatDate) ?? '';
  }

  getMsgDialogByLanguage(): string {
    let msgKey = '';
    // 1.	ユーザ共通情報.data.aswContext.lang=“ja”(日本語)、またはユーザ共通情報.data.aswContext.lang=“en”(英語)の場合、遷移先が外部サイトになる旨の確認ダイアログを表示する。
    if (
      this._common.aswContextStoreService.aswContextData.lang === 'ja' ||
      this._common.aswContextStoreService.aswContextData.lang === 'en'
    ) {
      // メッセージID = ”MSG0649” AND
      // 言語コード = ユーザ共通情報.data.aswContext.lang
      msgKey = 'MSG0649';
    } else {
      // 2.	ユーザ共通情報.data.aswContext.lang≠“ja”(日本語)、かつユーザ共通情報.data.aswContext.lang≠“en”(英語)の場合、遷移先が英語の外部サイトになる旨の確認ダイアログを表示する。
      // メッセージID = ”MSG0650” AND 言語コード = ユーザ共通情報.data.aswContext.lang
      msgKey = 'MSG0650';
    }
    return msgKey;
  }

  get getFunctionList(): { [key: string]: Function } {
    return {
      clickFareRuleDetailsModal: () => this.clickFareRuleDetailsModal(),
      clickBaggageApplication: () => this.clickBaggageApplication(),
      // (5)「支払情報詳細モーダル表示」ボタン押下時
      clickOpenPaymentDetailsModal: () => this.clickOpenPaymentDetailsModal(),
      clickSeatReservationStatus: (flightId: string) => this.clickSeatReservationStatus(flightId),
      clickGuidanceToDeparture: (airBoundId: string, flightID: string) =>
        this.clickGuidanceToDeparture(airBoundId, flightID),
      clickGuidanceToDestination: (airBoundId: string, flightID: string) =>
        this.clickGuidanceToDestination(airBoundId, flightID),
    };
  }

  /** 画面終了時処理 */
  destroy() {
    this._servicingCommonStoreService.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.MYBOOKING_ITINERARY,
    });
    this._subscriptions.unsubscribe();
  }

  /** 画面更新時処理 */
  reload() {}

  /** 共通No.1 URLパラメータ作成処理 */
  onHandleCommonNo1(airBoundId: string, flightID: string, fligthType: number = 2) {
    /**
     * 1. 以下の条件でASWDB(マスタ)からプロパティを取得する
     * テーブル: プロパティ
     * キャッシュキー: プロパティ.キー
     * キャッシュキー作成条件: －
     * 検索条件: プロパティ.カテゴリ="servicing"かつプロパティ.キー = "url.tabiCube"
     * ソート順: -
     */
    const urlTabiCube = encodeURI(this._common.aswMasterService.getMPropertyByKey('servicing', 'url.tabiCube'));

    /**
     * ※{lang}の値は以下のように設定する
     * 1)ユーザ共通情報.data.aswContext.lang=“ja”(日本語)の場合、"ja"
     * 2)上記以外の場合、"en"
     */
    const lang = this.aswLangCode === 'ja' ? 'ja' : 'en';

    /** 取得した結果に”{lang}/route/search”をつけて、空港アクセスナビURLとして保存する */
    let url = `${urlTabiCube}${lang}/route/search`;

    const bounds = parseEmptyArray(this.orderData?.air?.bounds);
    const bound: Bound =
      bounds.find((bound: Bound) => {
        return bound?.airBoundId == airBoundId;
      }) ?? ({} as Bound);

    let flightInfo =
      bound?.flights?.find((flight: Type1) => {
        return flight?.id == flightID;
      }) ?? ({} as Type1);

    // 1)PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].departure.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
    // 2)空港名が取得できなかった場合、PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].departure.locationName
    const depAirport = encodeURI(
      this._myBookingItineraryService.getAirportLocationName(
        flightInfo?.departure?.locationCode,
        flightInfo?.departure?.locationName
      )
    );

    // 1)PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].arrival.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
    // 2)空港名が取得できなかった場合、PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].arrival.locationName
    const arrAirport = encodeURI(
      this._myBookingItineraryService.getAirportLocationName(
        flightInfo?.arrival?.locationCode,
        flightInfo?.arrival?.locationName
      )
    );

    // PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].departure.terminalが存在する場合、PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].departure.terminal 上記以外の場合、””(空欄)
    const fromTerminal = this.getTerminal(flightInfo?.departure?.terminal);
    // PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].arrival.terminalが存在する場合、PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].arrival.terminal 上記以外の場合、””(空欄)
    const toTerminal = this.getTerminal(flightInfo?.arrival?.terminal);

    // 2.	空港アクセスナビのサイトを別タブで表示する。
    // ※パラメータの詳細については、【MaaS】フロントエンド外部流入URL・パラメータ定義_202309DXコメント追記.xlsx を参照。
    const parameters = {
      // PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].marketingAirlineCode + PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].marketingFlightNumber
      flightNumber:
        stringEmptyDefault(flightInfo?.marketingAirlineCode) + stringEmptyDefault(flightInfo?.marketingFlightNumber),
      // PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].departure.locationCode
      fromAirportCode: flightInfo?.departure?.locationCode ?? '',
      // PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].arrival.locationCode
      toAirportCode: flightInfo?.arrival?.locationCode ?? '',
      // PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].departure.dateTime (⽇本時間) ※yyyyMMddHHmm形式の⽂字列
      std: this.getDateTime(flightInfo?.departure?.dateTime, BookingDateFormat.yyyyMMddHHmm),
      // PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].arrival.dateTime (⽇本時間) ※yyyyMMddHHmm形式の⽂字列
      sta: this.getDateTime(flightInfo?.arrival?.dateTime, BookingDateFormat.yyyyMMddHHmm),
      referrer: this.getReferrer(),
      // 1)入力パラメータ.対象便種別="1"(出発便)の場合、false (出発空港""着""経路検索)
      // 2)入力パラメータ.対象便種別="2"(到着便)の場合、true (到着空港""発""経路検索)
      isArrivalGuide: fligthType === 1 ? false : true,
      // ds1(搭乗制限コード)
      ...(this.isNeedSettingDs1(flightInfo) && {
        ds1: this.getDs1(bound),
        ...(this.isNeedSettingDs2ToDs11(bound) && {
          // ds2
          ...(this.getDs2() !== '' && { ds2: this.getDs2() }),
          // ds3
          ...(this.getDs3() !== '' && { ds3: this.getDs3() }),
          // ds4
          ...(this.getDs4() !== '' && { ds4: this.getDs4() }),
          // ds5
          ...(this.getDs5() !== '' && { ds5: this.getDs5() }),
          // ds6
          ...(this.getDs6() !== '' && { ds6: this.getDs6() }),
          // ds7
          ...(this.getDs7() !== '' && { ds7: this.getDs7() }),
          // ds8
          ...(this.getDs8() !== '' && { ds8: this.getDs8() }),
          // ds9
          ...(this.getDs9() !== '' && { ds9: this.getDs9() }),
          // ds10
          ...(this.getDs10() !== '' && { ds10: this.getDs10() }),
          // ds11
          ...(this.getDs11() !== '' && { ds11: this.getDs11() }),
        }),
      }),
    };

    if (parameters) {
      url +=
        '?' +
        new URLSearchParams(parameters as any).toString() +
        `&depAirport=${depAirport}&arrAirport=${arrAirport}&fromTerminal=${fromTerminal}&toTerminal=${toTerminal}`;
    }

    window.open(url, '_blank');
  }

  isNeedSettingDs1(flightInfo: Type1) {
    // "△（PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.flights[入力パラメータ.セグメントID].isJapanDomesticFlight=true(国内線)の場合）"
    return flightInfo?.isJapanDomesticFlight === true;
  }

  isNeedSettingDs2ToDs11(bound: Bound) {
    const ds1 = this.getDs1(bound);
    // △(ds1(搭乗制限コード)に文字"3"を含む場合)
    if (ds1.indexOf('3') !== -1) {
      return true;
    }
    return false;
  }

  getDs1(bound: Bound) {
    let specCodeArr: Array<number> = [];
    //＜以下、搭乗者IDリストの要素数繰り返し＞（それぞれの要素が当該搭乗者ID）>>>> Handle by some() function to Optimize
    // 1)PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.walking.degreeSpecialServiceRequestの内に、codeが"WCHR"である場合、"1"(階段の昇り降りはできるが、長距離の歩行はできない)を設定する。
    const isHaveCodeWCHR = this.travelers.some((traveler) => {
      return (
        parseEmptyArray(
          (this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.walking
            ?.degreeSpecialServiceRequest
        ) as any[]
      ).some((item) => {
        return item?.code === UpdateBasicInformationSupportRequest.DegreeSpecialServiceRequestEnum.Wchr;
      });
    });
    if (isHaveCodeWCHR) {
      specCodeArr.push(1);
    }
    // 2)PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.walking.degreeSpecialServiceRequestの内に、codeが"WCHS"である場合、"2"(機内の移動はできるが、階段の昇り降りはできない)を設定する。
    const isHaveCodeWCHS = this.travelers.some((traveler) => {
      return (
        parseEmptyArray(
          (this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.walking
            ?.degreeSpecialServiceRequest
        ) as any[]
      ).some((item) => {
        return item?.code === UpdateBasicInformationSupportRequest.DegreeSpecialServiceRequestEnum.Wchs;
      });
    });
    if (isHaveCodeWCHS) {
      specCodeArr.push(2);
    }
    // 3)PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.walking.degreeSpecialServiceRequestの内に、codeが"WCHC"である場合、"3"(歩行ができない(機内の座席近くまで車いすを利用し、移乗の介助が必要))を設定する。
    const isHaveCodeWCHC = this.travelers.some((traveler) => {
      return (
        parseEmptyArray(
          (this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.walking
            ?.degreeSpecialServiceRequest
        ) as any[]
      ).some((item) => {
        if (item?.code === UpdateBasicInformationSupportRequest.DegreeSpecialServiceRequestEnum.Wchc) {
          this.wheelchairInformationList.push(
            (this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.walking?.wheelChair
          );
          return true;
        }
        return false;
      });
    });
    if (isHaveCodeWCHC) {
      specCodeArr.push(3);
    }
    // 4)PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.blind.ids[]が空でない場合、"4"(目に不自由がある)を設定する。
    const isHaveBlindIdArrNotEmpty = this.travelers.some((traveler) => {
      return (
        parseEmptyArray((this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.blind?.ids)
          .length !== 0
      );
    });
    if (isHaveBlindIdArrNotEmpty) {
      specCodeArr.push(4);
    }
    // 5)PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.deaf.ids[]が空でない場合、"5"(耳や言葉に不自由がある)を設定する。
    const isHaveDeafIdArrNotEmpty = this.travelers.some((traveler) => {
      return (
        parseEmptyArray((this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.deaf?.ids)
          .length !== 0
      );
    });
    if (isHaveDeafIdArrNotEmpty) {
      specCodeArr.push(5);
    }

    // 6)PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.pregnant.condition[]の内に、"meda"である場合、"6"(機内で医療用酸素ボンベ・酸素濃縮器・人工呼吸器の使用が必要である(診断書が必要))を設定する。
    const isHavePregnantConditionMeda = this.travelers.some((traveler) => {
      return (
        parseEmptyArray(
          (this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.pregnant?.condition
        ) as string[]
      ).includes('meda');
    });
    if (isHavePregnantConditionMeda) {
      specCodeArr.push(6);
    }
    // 7)PNR情報取得API応答.data.air.bounds<airBoundId=入力パラメータ.バウンドID>.originDepartureDateTime-PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.pregnant.dueDate[0]≤28日の場合、"7"(PGNT 妊娠中で出産予定日を含め28日以内のご搭乗である(診断書が必要))を設定する。
    const originDepartureDateTime = bound?.originDepartureDateTime ?? '';
    const isHavePregnantDueDateLessThanEqual28 = this.travelers.some((traveler) => {
      const pregnantDueDate =
        (this._myBookingStoreService.myBooking?.travelerSupports as any)?.[traveler?.id!]?.pregnant?.dueDate ?? '';
      return this.isDateDifferenceLessThanEqual_28(originDepartureDateTime, pregnantDueDate);
    });
    if (isHavePregnantDueDateLessThanEqual28) {
      specCodeArr.push(7);
    }
    // 8)上記以外の場合、"8"を設定する。
    if (specCodeArr.length === 0) {
      specCodeArr.push(8);
    }
    // ※複数のコードが設定される可能性がある（例：車いすを利用し、かつ、目が不自由なお客さま、など）。その場合の設定値の区切り文字は"_"とする"
    // 設定例：1_5_7
    // ※複数のコードに文字"3"を含む場合、PNR情報取得API応答.data.travelerSupports.<当該搭乗者ID>.walking.wheelChairを車いす情報リストに追加する。>>> processed in section 3)
    // ※搭乗者IDリストの要素数≥2件の場合、複数のコードの値の区切り文字は"_"とする"
    return specCodeArr.join('_');
  }

  getDs2() {
    // 1)車いす情報リスト[0].isNeeded=falseの場合、"0"(車いすの持ち込みなし)を設定する。
    const isHaveNotWheelchairProvided = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.isNeeded === false;
    });

    if (isHaveNotWheelchairProvided) {
      return '0';
    }
    // 2)車いす情報リスト[0].isNeeded=trueの場合、1"(車いすの持ち込みあり)を設定する。
    const isHaveWheelchairProvided = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.isNeeded === true;
    });

    if (isHaveWheelchairProvided) {
      return '1';
    }
    // 3)上記以外の場合、設定しない。
    return '';
  }

  getDs3() {
    // 1)車いす情報リスト[0].isFoldable=falseの場合、"0"(車いすの折り畳み不可)を設定する。
    const isHaveWheelchairCannotBeFolded = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.isFoldable === false;
    });

    if (isHaveWheelchairCannotBeFolded) {
      return '0';
    }
    // 2)車いす情報リスト[0].isFoldable=trueの場合、1"(車いすの折り畳み可)を設定する。
    const isHaveWheelchairCanBeFolded = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.isFoldable === true;
    });

    if (isHaveWheelchairCanBeFolded) {
      return '1';
    }
    // 3)上記以外の場合、設定しない。
    return '';
  }

  getDs4() {
    // 1)車いす情報リスト[0].type="manual"の場合、"M"(手動)を設定する。
    const isHaveTypeManual = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.type === 'manual';
    });

    if (isHaveTypeManual) {
      return 'M';
    }
    // 2)車いす情報リスト[0].type="battery"の場合、"E"(電動)を設定する。
    const isHaveTypeBattery = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.type === 'battery';
    });

    if (isHaveTypeBattery) {
      return 'E';
    }
    // 3)上記以外の場合、設定しない。
    return '';
  }

  getDs5() {
    // 1)車いす情報リスト[0].batteryType="nickelCadmium"の場合、"1"(NICD ニッカド)を設定する。
    const isHaveBatteryTypeNickelCadmium = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.batteryType === 'nickelCadmium';
    });

    if (isHaveBatteryTypeNickelCadmium) {
      return '1';
    }
    // 2)車いす情報リスト[0].batteryType="nickelMetalHydride"の場合、"2"(NIMH ニッケル水素)を設定する。
    const isHaveBatteryTypeNickelMetalHydride = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.batteryType === 'nickelMetalHydride';
    });

    if (isHaveBatteryTypeNickelMetalHydride) {
      return '2';
    }
    // 3)車いす情報リスト[0].batteryType="lithiumIon"の場合、"3"(LITHIUMION リチウムイオン)を設定する。
    const isHaveBatteryTypeLithiumIon = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.batteryType === 'lithiumIon';
    });

    if (isHaveBatteryTypeLithiumIon) {
      return '3';
    }

    // 4)車いす情報リスト[0].batteryType="shieldBattery"の場合、"4"(SHIELD シールド)を設定する。
    const isHaveBatteryTypeShieldBattery = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.batteryType === 'shieldBattery';
    });

    if (isHaveBatteryTypeShieldBattery) {
      return '4';
    }

    // 5)車いす情報リスト[0].batteryType="nonShieldBattery"の場合、"5"(NONSHIELD ノンシールド)を設定する。
    const isHaveBatteryTypeNonShieldBattery = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.batteryType === 'nonShieldBattery';
    });

    if (isHaveBatteryTypeNonShieldBattery) {
      return '4';
    }
    // 6)上記以外の場合、設定しない。
    return '';
  }

  getDs6() {
    // 1)車いす情報リスト[0].depth=0、またはdepthが存在しない場合、設定しない。
    const isHaveWheelChairEmpty = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.depth === undefined || item?.depth === 0;
    });

    if (isHaveWheelChairEmpty) {
      return '';
    }
    // 2)上記以外の場合、車いす情報リスト[0].depthを設定する。
    return (this.wheelchairInformationList[0] as any[])?.[0]?.depth ?? '';
  }

  getDs7() {
    // 1)車いす情報リスト[0].width=0、またはwidthが存在しない場合、設定しない。
    const isHaveWheelChairWidthEmpty = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.width === undefined || item?.width === 0;
    });

    if (isHaveWheelChairWidthEmpty) {
      return '';
    }
    // 2)上記以外の場合、車いす情報リスト[0].widthを設定する。
    return (this.wheelchairInformationList[0] as any[])?.[0]?.width ?? '';
  }

  getDs8() {
    // 1)車いす情報リスト[0].height=0、またはheightが存在しない場合、設定しない。
    const isHaveWheelChairHeightEmpty = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.height === undefined || item?.height === 0;
    });

    if (isHaveWheelChairHeightEmpty) {
      return '';
    }
    // 2)上記以外の場合、車いす情報リスト[0].heightを設定する。
    return (this.wheelchairInformationList[0] as any[])?.[0]?.height ?? '';
  }

  getDs9() {
    // 1)車いす情報リスト[0].depth、かつwidth、かつheightが空である場合、設定しない。
    if (this.getDs6() === '' && this.getDs7() === '' && this.getDs8() === '') {
      return '';
    }
    // 2)上記以外の場合、"1"(cm)を設定する。
    return '1';
  }

  getDs10() {
    // 1)車いす情報リスト[0].weight=0、またはweightが存在しない場合、設定しない。
    const isHaveWheelChairWeighEmpty = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.weight === undefined || item?.weight === 0;
    });

    if (isHaveWheelChairWeighEmpty) {
      return '';
    }
    // 2)上記以外の場合、車いす情報リスト[0].weightを設定する。
    return (this.wheelchairInformationList[0] as any[])?.[0]?.weight ?? '';
  }

  // QnA
  getDs11() {
    // 1)車いす情報リスト[0].weight=0、またはweightが存在しない場合、設定しない。
    const isHaveWheelChairWeightEmpty = (this.wheelchairInformationList[0] as any[]).some((item) => {
      return item?.weight === undefined || item?.weight === 0;
    });

    if (isHaveWheelChairWeightEmpty) {
      return '';
    }
    // 2)上記以外の場合、"1"(kg)を設定する。
    return '1';
  }

  isDateDifferenceLessThanEqual_28(originDepartureDateTime: string, dueDate: string): boolean {
    const date1 = new Date(originDepartureDateTime);
    const date2 = new Date(dueDate);

    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      return false;
    }

    const timeDifference = Math.abs(date1.getTime() - date2.getTime());
    const dayDifference = timeDifference / (1000 * 3600 * 24);

    return dayDifference <= 28;
  }

  getTerminal(terminal: string | undefined) {
    return terminal
      ? encodeURI(
          this._staticMsgPipe.transform('m_static_message-label.terminal.departure', {
            '0': terminal,
          })
        )
      : '';
  }
}
